import React, { useRef, useState } from 'react';
import DataTableHeaderDrawer from './DataTableHeaderDrawer';
import DataContextMenu from './DataContextMenu';
import DownChevronIcon from '../../../../assets/images/svg/DownChevronIcon';
import InfoIcon from '../../../../assets/images/svg/InfoIcon';
import { ContextMessage, SortAction } from '../types';
import { Tooltip, ToolTipDir } from '../../../../shared/components/tooltip';
import { SearchInput } from '../../../../shared/components/inputs';
import { Transition } from 'react-transition-group';
import {
  DropDown,
  HiddenDropDown,
} from '../../../../shared/components/dorpDown';

const duration = 300;

const defaultStyle: React.CSSProperties = {
  transition: `all ${duration}ms ease-in-out`,
  opacity: 0,
  maxHeight: 0,
};

const transitionStyles: Record<string, React.CSSProperties> = {
  entering: { opacity: 1, maxHeight: '100px' },
  entered: { opacity: 1, maxHeight: '100px' },
  exiting: { opacity: 0, maxHeight: 0 },
  exited: { opacity: 0, maxHeight: 0 },
};

type DataTableHeaderProps<T> = {
  title?: string | React.ReactNode;
  actions: React.ReactNode | React.ReactNode[];
  children?: React.ReactNode;
  filterActions?: React.ReactNode | React.ReactNode[];
  selectedCount: number;
  showMenu?: boolean;
  contextMessage: ContextMessage;
  tooltip?: { label: string; dir?: ToolTipDir };
  search?: boolean;
  searchPropmt?: string;
  hiddenDropDown?: boolean;
  items?: { label: string; value: string }[];
  dropDown?: boolean;
  dropDownItems?: { label: string; value: string }[];
  onSort: (action: SortAction<T>) => void;
  onSearchChange: (arg0: string) => void;
};

function DataTableHeader<T>({
  title,
  actions = null,
  filterActions = null,
  children,
  selectedCount,
  showMenu,
  contextMessage,
  tooltip,
  search,
  searchPropmt,
  onSearchChange,
  hiddenDropDown,
  items,
  dropDownItems,
  dropDown,
}: DataTableHeaderProps<T>): JSX.Element {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const toggleDrawerOpen = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const filterable = !!filterActions;

  const nodeRef = useRef(null);

  return (
    <div>
      <div
        className='relative box-border flex w-full flex-wrap items-center justify-between border-b border-gray-light px-8 py-6'
        style={{ flex: '1 1 auto' }}
        role='heading'
        aria-level={1}
      >
        {hiddenDropDown ? (
          <div className='flex text-xl font-bold text-gray-darkest'>
            <button
              type='button'
              onClick={toggleOpen}
              className='flex flex-row items-center text-lg font-semibold tracking-tight focus:outline-none'
            >
              {typeof title === 'string' ? (
                <span className='text-lg font-bold tracking-tight'>
                  {title}
                </span>
              ) : (
                title
              )}
              {tooltip && (
                <div className='ml-2'>
                  <Tooltip label={`${tooltip.label}`} dir={tooltip.dir}>
                    <InfoIcon />
                  </Tooltip>
                </div>
              )}
              <div
                className={`transform bg-transparent text-gray-dark transition duration-300 ease-in ${
                  isOpen ? 'rotate-180' : 'rotate-0'
                }`}
              >
                <DownChevronIcon width='27px' height='27px' strokeWidth='3' />
              </div>
            </button>
            <HiddenDropDown
              items={items}
              isOpen={isOpen}
              setIsOpen={toggleOpen}
            />
          </div>
        ) : filterable ? (
          <div className='text-xl font-bold text-gray-darkest'>
            <button
              type='button'
              onClick={toggleDrawerOpen}
              className='flex flex-row items-center text-lg font-semibold tracking-tight focus:outline-none'
            >
              {typeof title === 'string' ? (
                <span className='text-lg font-bold tracking-tight'>
                  {title}
                </span>
              ) : (
                title
              )}
              {tooltip && (
                <div className='-mr-2 ml-2'>
                  <Tooltip label={tooltip.label} dir={tooltip.dir}>
                    <InfoIcon />
                  </Tooltip>
                </div>
              )}
              <div
                className={`ml-2 transform transition-all duration-300  ease-in-out ${
                  isDrawerOpen ? 'rotate-180' : 'rotate-0'
                }`}
              >
                <DownChevronIcon width='28' height='28' strokeWidth='3' />
              </div>
            </button>
          </div>
        ) : typeof title === 'string' ? (
          <div className='flex flex-row items-center text-xl font-bold text-gray-darkest'>
            <span className='text-lg font-bold tracking-tight'>{title}</span>

            {tooltip && (
              <div className='-mr-2 ml-2'>
                <Tooltip label={tooltip.label} dir={tooltip.dir}>
                  <InfoIcon />
                </Tooltip>
              </div>
            )}
          </div>
        ) : (
          title
        )}
        <div className='flex'>
          {search && (
            <div className='flex items-center justify-end'>
              <SearchInput
                classes={actions ? ['mr-8'] : []}
                name='search'
                placeholder={searchPropmt ? searchPropmt : `Search...`}
                onDebouncedChange={onSearchChange}
                delay={300}
              />
            </div>
          )}
          {actions && (
            <div className='flex items-center justify-end'>{actions}</div>
          )}
          {dropDown && <DropDown title='Sort' items={dropDownItems} />}
        </div>
        {showMenu && (
          <DataContextMenu
            contextMessage={contextMessage}
            selectedCount={selectedCount}
          />
        )}
        {children && children}
      </div>
      {filterable && (
        <Transition in={isDrawerOpen} timeout={500} nodeRef={nodeRef}>
          {(state) => (
            <div
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              style={{
                ...defaultStyle,
                ...transitionStyles[state],
              }}
            >
              <DataTableHeaderDrawer filterActions={filterActions} />
            </div>
          )}
        </Transition>
      )}
    </div>
  );
}

export default DataTableHeader;
